<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  UiDialog,
  UiSelect,
  UiMultiSelectCombobox,
  UiCurrencyInput,
  UiNumberField,
  UiSwitch,
  UiTextarea,
  UiText,
  UiButton,
  UiInfoBox,
} from "@shopexperts/shared";
import type {
  AdminCreateWalletTransactionRequest,
  AdminWalletTransactionKind,
  AdminWalletHoldSource,
} from "@shopexperts/legacy-api";
import {
  useAdminOrganizationWalletHoldTransactions,
  useCreateAdminWalletTransaction,
} from "@/app/composables/wallet";
import { dollarsToCents } from "@/app/composables/expert-levels/expertLevelForm";
import { formatCurrency } from "@/v2Projects/utils/display";
import {
  buildHoldDebitAllocations,
  preserveHoldSelectionOrder,
  type HoldDebitAllocation,
} from "./holdDebitAllocation";

const props = defineProps<{
  organizationId: number | null | undefined;
}>();

const emit = defineEmits<{
  (e: "created"): void;
}>();

const open = defineModel<boolean>({ default: false });

const { mutateAsync: createTransaction, isPending } = useCreateAdminWalletTransaction();

const kind = ref<AdminWalletTransactionKind>("admin_credit");
const holdSource = ref<AdminWalletHoldSource>("available");
const selectedHoldIds = ref<string[]>([]);
const amountDollars = ref<number>(0);
const grossAmountDollars = ref<number>(0);
const netAmountDollars = ref<number>(0);
const projectSettled = ref(false);
const expertAlreadyPaid = ref(false);
const quoteId = ref("");
const publicNotes = ref("");
const internalNotes = ref("");
const errorMessage = ref<string | null>(null);
const idempotencyKey = ref<string | null>(null);

const kindOptions: { label: string; value: AdminWalletTransactionKind }[] = [
  { label: "Credit available balance", value: "admin_credit" },
  { label: "Debit available balance", value: "admin_debit" },
  { label: "Add/Move balance to hold", value: "admin_hold" },
  { label: "Remove funds from hold", value: "admin_hold_debit" },
  { label: "Record external payment", value: "admin_external_payment" },
  { label: "Gross sales credit (level only)", value: "admin_gross_sales_credit" },
  { label: "Gross sales debit (level only)", value: "admin_gross_sales_debit" },
  { label: "Net credit (lifetime only)", value: "admin_net_credit" },
  { label: "Net debit (lifetime only)", value: "admin_net_debit" },
];

const holdSourceOptions: { label: string; value: AdminWalletHoldSource }[] = [
  { label: "Move from available balance", value: "available" },
  { label: "Direct hold (no available debit)", value: "direct" },
];

const kindHelp: Record<AdminWalletTransactionKind, string> = {
  admin_credit: "Adds funds directly to the spendable available balance.",
  admin_debit: "Removes funds from the spendable available balance.",
  admin_hold: "Reserves funds on hold so they cannot be paid out yet.",
  admin_hold_debit: "Removes held funds without adding them to the available balance.",
  admin_external_payment:
    "Records an externally collected payment using the supplied gross and expert net amounts.",
  admin_gross_sales_credit:
    "Increases lifetime gross sales only — adjusts level progress, not the balance.",
  admin_gross_sales_debit:
    "Decreases lifetime gross sales only — adjusts level progress, not the balance.",
  admin_net_credit:
    "Increases lifetime net value only — adjusts reporting totals, not the balance.",
  admin_net_debit:
    "Decreases lifetime net value only — adjusts reporting totals, not the balance.",
};

const isHold = computed(() => kind.value === "admin_hold");
const isHoldDebit = computed(() => kind.value === "admin_hold_debit");
const isExternalPayment = computed(() => kind.value === "admin_external_payment");
const currentHelp = computed(() => kindHelp[kind.value]);
const shouldLoadHoldTransactions = computed(() => open.value && isHoldDebit.value);

const {
  transactions: holdTransactions,
  isLoading: isLoadingHoldTransactions,
  isFetching: isFetchingHoldTransactions,
  error: holdTransactionsError,
  refetch: refetchHoldTransactions,
} = useAdminOrganizationWalletHoldTransactions({
  organizationId: computed(() => props.organizationId),
  enabled: shouldLoadHoldTransactions,
});

const holdOptions = computed(() =>
  holdTransactions.value.map((transaction) => ({
    value: String(transaction.id),
    label: [
      `#${transaction.id}`,
      `Releasable ${formatCurrency(transaction.remaining_hold_cents ?? 0, transaction.currency)}`,
      `Hold ${formatCurrency(transaction.hold_delta_cents, transaction.currency)}`,
    ].join(" · "),
  })),
);

const requestedHoldDebitCents = computed(() => {
  const cents = dollarsToCents(amountDollars.value);
  return Number.isFinite(cents) && cents > 0 ? cents : 0;
});

const selectedHoldAllocations = computed(() =>
  buildHoldDebitAllocations(
    holdTransactions.value,
    selectedHoldIds.value,
    requestedHoldDebitCents.value,
  ),
);

const selectedReleasableCents = computed(() =>
  selectedHoldAllocations.value.reduce(
    (total, allocation) => total + allocation.releasableCents,
    0,
  ),
);

const allocatedHoldDebitCents = computed(() =>
  selectedHoldAllocations.value.reduce(
    (total, allocation) => total + allocation.debitedCents,
    0,
  ),
);
const unallocatedHoldDebitCents = computed(() =>
  Math.max(0, requestedHoldDebitCents.value - allocatedHoldDebitCents.value),
);
const holdCurrency = computed(
  () => selectedHoldAllocations.value[0]?.transaction.currency ?? "USD",
);
const selectedReleasableLabel = computed(() =>
  formatCurrency(selectedReleasableCents.value, holdCurrency.value),
);
const holdComboboxHint = computed(() =>
  selectedHoldIds.value.length > 0
    ? `${selectedHoldIds.value.length} selected · ${selectedReleasableLabel.value} total releasable`
    : "Search by transaction ID, then select one or more holds in the order they should be consumed.",
);
const allocationPreviewVariant = computed(() =>
  unallocatedHoldDebitCents.value > 0 ? "warning" as const : "info" as const,
);
const allocationPreviewText = computed(() => {
  if (requestedHoldDebitCents.value === 0) {
    return "Enter an amount to preview how it will be split across the selected holds.";
  }
  if (unallocatedHoldDebitCents.value > 0) {
    return `The selected holds are short by ${formatCurrency(unallocatedHoldDebitCents.value, holdCurrency.value)}. Select another hold or lower the amount.`;
  }
  return "The amount will be removed from these holds from top to bottom. The last used hold may be partially debited.";
});

const externalPaymentEffect = computed(() => {
  if (expertAlreadyPaid.value) {
    return "Records the gross and net lifetime totals without adding funds to the wallet.";
  }
  if (projectSettled.value) {
    return "Adds the net amount to the expert's available balance.";
  }
  return "Adds the net amount to hold until it is released or debited.";
});

function updateSelectedHoldIds(nextIds: string[]) {
  selectedHoldIds.value = preserveHoldSelectionOrder(selectedHoldIds.value, nextIds);
}

function allocationResultLabel(allocation: HoldDebitAllocation): string {
  if (requestedHoldDebitCents.value === 0) return "Waiting for amount";
  if (allocation.status === "unused") return "Not reached";
  if (allocation.status === "fully_debited") return "Fully debited";
  return `${formatCurrency(allocation.remainingCents, allocation.transaction.currency)} remains`;
}

watch(open, (isOpen) => {
  if (isOpen) {
    idempotencyKey.value = null;
    kind.value = "admin_credit";
    holdSource.value = "available";
    selectedHoldIds.value = [];
    amountDollars.value = 0;
    grossAmountDollars.value = 0;
    netAmountDollars.value = 0;
    projectSettled.value = false;
    expertAlreadyPaid.value = false;
    quoteId.value = "";
    publicNotes.value = "";
    internalNotes.value = "";
    errorMessage.value = null;
  }
});

watch(kind, (nextKind) => {
  if (nextKind !== "admin_hold_debit") selectedHoldIds.value = [];
});

function generateIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `admin-${crypto.randomUUID()}`;
  }
  return `admin-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function submit() {
  errorMessage.value = null;

  if (typeof props.organizationId !== "number" || props.organizationId <= 0) {
    errorMessage.value = "This expert has no linked organization wallet.";
    return;
  }

  const amountCents = dollarsToCents(amountDollars.value);
  const grossAmountCents = dollarsToCents(grossAmountDollars.value);
  const netAmountCents = dollarsToCents(netAmountDollars.value);
  const parsedQuoteId = quoteId.value.trim() === "" ? null : Number(quoteId.value);

  if (isExternalPayment.value) {
    if (!Number.isFinite(grossAmountCents) || grossAmountCents < 1) {
      errorMessage.value = "Enter a gross amount greater than zero.";
      return;
    }
    if (!Number.isFinite(netAmountCents) || netAmountCents < 0) {
      errorMessage.value = "Enter a net amount of zero or more.";
      return;
    }
    if (netAmountCents > grossAmountCents) {
      errorMessage.value = "Net amount can't exceed the gross amount.";
      return;
    }
    if (parsedQuoteId !== null && (!Number.isInteger(parsedQuoteId) || parsedQuoteId < 1)) {
      errorMessage.value = "Quote ID must be a positive whole number.";
      return;
    }
  } else if (!Number.isFinite(amountCents) || amountCents < 1) {
    errorMessage.value = "Enter an amount greater than zero.";
    return;
  }

  if (isHoldDebit.value) {
    if (selectedHoldIds.value.length === 0) {
      errorMessage.value = "Select at least one releasable hold transaction.";
      return;
    }
    if (amountCents > selectedReleasableCents.value) {
      errorMessage.value = `Amount can't exceed the selected releasable total (${selectedReleasableLabel.value}).`;
      return;
    }
  }

  try {
    const transactionKind = kind.value;
    const requestIdempotencyKey = idempotencyKey.value ?? generateIdempotencyKey();
    idempotencyKey.value = requestIdempotencyKey;
    const commonNotes = {
      amount_cents: amountCents,
      idempotency_key: requestIdempotencyKey,
      public_notes: publicNotes.value.trim() || null,
      internal_notes: internalNotes.value.trim() || null,
    };
    const body: AdminCreateWalletTransactionRequest =
      transactionKind === "admin_external_payment"
        ? {
            transaction_kind: transactionKind,
            gross_amount_cents: grossAmountCents,
            net_amount_cents: netAmountCents,
            project_settled: projectSettled.value,
            expert_already_paid: expertAlreadyPaid.value,
            idempotency_key: requestIdempotencyKey,
            ...(parsedQuoteId !== null ? { quote_id: parsedQuoteId } : {}),
            public_notes: publicNotes.value.trim() || null,
            internal_notes: internalNotes.value.trim() || null,
          }
        : transactionKind === "admin_hold_debit"
        ? {
            ...commonNotes,
            transaction_kind: transactionKind,
            hold_transaction_ids: selectedHoldIds.value.map(Number),
          }
        : {
            ...commonNotes,
            transaction_kind: transactionKind,
            ...(transactionKind === "admin_hold" ? { hold_source: holdSource.value } : {}),
          };

    await createTransaction({
      organizationId: props.organizationId,
      body,
    });
    open.value = false;
    emit("created");
  } catch {
    // Error toast handled by the composable.
  }
}

function cancel() {
  open.value = false;
}
</script>

<template>
  <UiDialog
    v-model="open"
    v-model:loading="isPending"
    title="New wallet transaction"
    size="lg"
    hide-scrollbar
  >
    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Transaction type" />
        <UiSelect
          :model-value="kind"
          :options="kindOptions"
          size="md"
          :disabled="isPending"
          @update:model-value="(value) => (kind = value as AdminWalletTransactionKind)"
        />
        <UiText as="p" variant="p-small" class="text-se-gray-500" :text="currentHelp" />
      </div>

      <div v-if="isHold" class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Hold source" />
        <UiSelect
          :model-value="holdSource"
          :options="holdSourceOptions"
          size="md"
          :disabled="isPending"
          @update:model-value="(value) => (holdSource = value as AdminWalletHoldSource)"
        />
      </div>

      <template v-if="isExternalPayment">
        <div class="grid gap-4 sm:grid-cols-2">
          <div class="flex flex-col gap-1.5">
            <UiText as="label" variant="label" text="Gross amount" />
            <UiCurrencyInput
              v-model="grossAmountDollars"
              name="external-payment-gross"
              :min="0"
              :step="1"
              :disabled="isPending"
            />
          </div>

          <div class="flex flex-col gap-1.5">
            <UiText as="label" variant="label" text="Net amount to expert" />
            <UiCurrencyInput
              v-model="netAmountDollars"
              name="external-payment-net"
              :min="0"
              :max="grossAmountDollars"
              :step="1"
              :disabled="isPending"
            />
          </div>
        </div>

        <div class="flex flex-col gap-1.5">
          <UiText as="label" variant="label" text="External quote ID (optional)" />
          <UiNumberField
            v-model="quoteId"
            name="external-payment-quote-id"
            :min="1"
            :step="1"
            placeholder="Quote ID"
            hint="Must be an external quote offered by this expert's organization."
            :disabled="isPending"
          />
        </div>

        <div class="grid gap-3 sm:grid-cols-2">
          <div class="flex items-start gap-3 rounded-lg border border-se-gray-200 p-3">
            <UiSwitch
              v-model="projectSettled"
              name="external-payment-project-settled"
              size="sm"
              :disabled="isPending"
            />
            <div class="flex min-w-0 flex-col gap-0.5">
              <UiText as="span" variant="p-small" text="Project is settled" class="font-semibold text-se-dark" />
              <UiText
                as="p"
                variant="caption-normal"
                text="When the expert hasn't been paid, send the net amount to available instead of hold."
                class="text-se-gray-500"
              />
            </div>
          </div>

          <div class="flex items-start gap-3 rounded-lg border border-se-gray-200 p-3">
            <UiSwitch
              v-model="expertAlreadyPaid"
              name="external-payment-expert-paid"
              size="sm"
              :disabled="isPending"
            />
            <div class="flex min-w-0 flex-col gap-0.5">
              <UiText as="span" variant="p-small" text="Expert already paid" class="font-semibold text-se-dark" />
              <UiText
                as="p"
                variant="caption-normal"
                text="Record lifetime totals without adding the net amount to available or hold."
                class="text-se-gray-500"
              />
            </div>
          </div>
        </div>

        <UiInfoBox variant="info" title="Wallet effect">
          <UiText as="span" variant="p-small" :text="externalPaymentEffect" />
        </UiInfoBox>
      </template>

      <div v-if="isHoldDebit" class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Hold transactions" />

        <UiText
          v-if="isLoadingHoldTransactions || (isFetchingHoldTransactions && holdOptions.length === 0)"
          as="p"
          variant="p-small"
          class="text-se-gray-500"
          text="Loading releasable hold transactions…"
        />

        <UiInfoBox
          v-else-if="holdTransactionsError"
          variant="error"
          title="Couldn't load hold transactions"
        >
          <div class="flex items-center justify-between gap-3">
            <UiText
              as="span"
              variant="p-small"
              text="Try loading the releasable holds again."
            />
            <UiButton
              variant="outline"
              size="sm"
              label="Retry"
              :disabled="isFetchingHoldTransactions"
              @click="() => refetchHoldTransactions()"
            />
          </div>
        </UiInfoBox>

        <UiInfoBox
          v-else-if="holdOptions.length === 0"
          variant="warning"
          title="No releasable holds"
        >
          <UiText
            as="span"
            variant="p-small"
            text="This wallet has no hold transactions with a remaining releasable amount."
          />
        </UiInfoBox>

        <UiMultiSelectCombobox
          v-else
          :model-value="selectedHoldIds"
          name="hold-transactions"
          :options="holdOptions"
          placeholder="Filter releasable holds by transaction ID"
          :hint="holdComboboxHint"
          :disabled="isPending"
          open-on-click
          show-chevron
          @update:model-value="updateSelectedHoldIds"
        />
      </div>

      <div v-if="!isExternalPayment" class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Amount" />
        <UiCurrencyInput v-model="amountDollars" :min="0" :step="1" :disabled="isPending" />
      </div>

      <UiInfoBox
        v-if="isHoldDebit && selectedHoldAllocations.length > 0"
        :variant="allocationPreviewVariant"
        title="Estimated allocation by selection order"
      >
        <div class="flex flex-col gap-3">
          <UiText as="p" variant="p-small" :text="allocationPreviewText" />

          <div class="divide-y divide-se-gray-200 border-y border-se-gray-200">
            <div
              v-for="(allocation, index) in selectedHoldAllocations"
              :key="allocation.transaction.id"
              class="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 py-2.5"
            >
              <UiText
                as="span"
                variant="caption"
                :text="String(index + 1)"
                class="flex size-6 items-center justify-center rounded-full bg-se-gray-100 text-se-gray-700"
              />

              <div class="min-w-0">
                <UiText
                  as="p"
                  variant="p-small"
                  class="font-semibold text-se-dark"
                  :text="`Hold transaction #${allocation.transaction.id}`"
                />
                <UiText
                  as="p"
                  variant="caption-normal"
                  class="text-se-gray-500"
                  :text="`Releasable ${formatCurrency(allocation.releasableCents, allocation.transaction.currency)}`"
                />
              </div>

              <div class="text-right">
                <UiText
                  as="p"
                  variant="p-small"
                  class="font-semibold"
                  :class="allocation.debitedCents > 0 ? 'text-se-danger' : 'text-se-gray-400'"
                  :text="allocation.debitedCents > 0
                    ? `−${formatCurrency(allocation.debitedCents, allocation.transaction.currency)}`
                    : '—'"
                />
                <UiText
                  as="p"
                  variant="caption-normal"
                  class="text-se-gray-500"
                  :text="allocationResultLabel(allocation)"
                />
              </div>
            </div>
          </div>

          <UiText
            as="p"
            variant="caption-normal"
            class="text-se-gray-500"
            text="To change the order, deselect a hold and select it again."
          />
        </div>
      </UiInfoBox>

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Public note (visible to expert)" />
        <UiTextarea
          v-model="publicNotes"
          :rows="2"
          placeholder="Optional note the expert will see on the transaction."
          :disabled="isPending"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Internal note (admin only)" />
        <UiTextarea
          v-model="internalNotes"
          :rows="2"
          placeholder="Optional internal context for the admin team."
          :disabled="isPending"
        />
      </div>

      <UiInfoBox v-if="isExternalPayment" variant="info">
        <UiText
          as="span"
          variant="p-small"
          text="The amounts are recorded exactly as entered; no processing fee or platform commission is calculated. An idempotency key is generated automatically to prevent duplicates."
        />
      </UiInfoBox>

      <UiInfoBox v-else variant="info">
        <UiText as="span" variant="p-small">
          <span>
            An idempotency key is generated automatically to prevent duplicate ledger entries.
            Lifetime adjustments change reporting totals only and do not move the balance.
          </span>
        </UiText>
      </UiInfoBox>

      <UiText v-if="errorMessage" as="p" variant="p-small" class="text-red-600" :text="errorMessage" />
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <UiButton variant="outline" label="Cancel" :disabled="isPending" @click="cancel" />
        <UiButton
          variant="primary"
          :label="isPending ? 'Creating…' : 'Create transaction'"
          :disabled="isPending"
          @click="submit"
        />
      </div>
    </template>
  </UiDialog>
</template>
